import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { User } from '../../models/user.model';
import { debounceTime, distinctUntilChanged, filter, Subscription, switchMap, tap } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from '../../../../core/services/auth.service';
import { JwtService } from '../../../../core/services/jwt.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SeoService } from '../../../services/seo.service';
import { GtmService } from '../../../../core/services/gtm.service';
import { environment } from '../../../../../environments/environment';
import { UserService } from '../../services/user.service';
import { UserPreference } from '../../models/user-preference.model';
import { FormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { SnackBarService } from '../../../services/snackbar.service';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatSlideToggleModule,
    FormsModule
  ],
  templateUrl: './profile.html',
  styleUrl: './profile.scss',
})
export class ProfileComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  protected authService = inject(AuthService);
  private jwtService = inject(JwtService);
  private platformId = inject(PLATFORM_ID);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  user: User | null = null;
  userId: string | null = null;
  userTotalLikes = 0;
  isLoading = true;
  private originalMarketingValue = false;
  private userService = inject(UserService);
  userPreference: UserPreference | null = null;
  private userSubscription: Subscription | undefined;
  private seo = inject(SeoService);
  private gtm = inject(GtmService);
  private snackbar = inject(SnackBarService);

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.gtm.pageView({
        page_path: '/profile',
        page_title: 'Profile | Horizinns'
      });

      this.userId = this.jwtService.getUserId();
      this.cdr.detectChanges(); // ← Trigger CD if needed
    }

    this.seo.updateMetaTags('Profile', 'User Profile for Horizinns', undefined, `${environment.appUrl}/profile`);

    this.authService.user$.pipe(
      filter(user => user !== undefined),
      debounceTime(0),
      distinctUntilChanged(),
      tap(user => {
        this.isLoading = false;

        if (user) {
          this.user = user;
          this.userId = user.username ?? this.jwtService.getUserId();
        } 
        this.cdr.detectChanges();
      }),
      filter(() => !!this.user),
      switchMap(() => this.userService.getUserPreferences(this.user?.id as string)),
      tap(prefs => {
        console.log('prefs', prefs);
        this.userPreference = prefs;
        this.originalMarketingValue = prefs.receiveMarketing;
        
        this.isLoading = false;
        this.cdr.detectChanges();
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  isPreferenceDirty(): boolean {
    if (!this.userPreference) return false;
    return this.userPreference.receiveMarketing !== this.originalMarketingValue;
  }

  // Save method
  savePreferences() {
    if (!this.userPreference || !this.user || !this.user.id) return;

    this.userService.updateUserPreferences(this.user.id as string, this.userPreference).subscribe({
      next: (updatedPrefs) => {
        this.userPreference = updatedPrefs;
        this.originalMarketingValue = updatedPrefs.receiveMarketing; // Reset dirty state
        console.log('Preferences updated!');
        this.snackbar.open('Preferences saved successfully', ' ', {
            duration: 5000,
            panelClass: ['success-snackbar'],
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
      },
      error: (err) => {
        console.error('Save failed', err);
        this.snackbar.open('There was a problem saving your preferences. Please try again.', ' ', {
            duration: 5000,
            panelClass: ['error-snackbar'],
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
      }
    });
  }

  onTotalLikesChange(count: number) {
    this.userTotalLikes = count;
    console.log('Parent received total likes:', count);
    // You can now use it in logic, API calls, etc.
  } 

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }
}
